import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from "@mui/material";
import { Theme } from '@mui/material/styles'
import { createStyles, makeStyles } from '@mui/styles';
import { useEffect, useState } from "react";
import {  Line } from "react-chartjs-2";
import GetVPSettlement from "../utils/GetSettlement";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * This will be used for 
 * @date 6/16/2022
 * @author 
 *
 * @interface VPSettlementProps
 * @typedef {VPSettlementProps}
 */
interface VPSettlementProps {
  /**
   * This will be used for 
   * @date 6/16/2022
   * @author 
   *
   * @type {string}
   */
  customerNum: string;
  /**
   * This will be used for 
   * @date 6/16/2022
   * @author 
   *
   * @type {string}
   */
  salesOrg: string;
  /**
   * This will be used for 
   * @date 6/16/2022
   * @author 
   *
   * @type {?*}
   */
  data?: any;
  /**
   * This will be used for 
   * @date 6/16/2022
   * @author 
   *
   * @type {?boolean}
   */
  hideAccordion?: boolean;
}

/**
 * This will be used for 
 * @date 6/16/2022
 * @author 
 *
 * @type {*}
 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    padLeft: {
      paddingBottom: theme.spacing(1)
    }
  }));

/**
 * This will be used for 
 * @date 6/16/2022
 * @author 
 *
 * @export
 * @param {VPSettlementProps} props
 * @returns {*}
 */
export default function VPSettlement(props: VPSettlementProps) {
  const classes = useStyles();
  const [state, setState] = useState<any[]>(props.data || []);
  const [isError, setIsError] = useState(false);
  /**
   * @Description Convert date and have duplicate axis
   * @author Srinadh
   * @param date:string
   * @param isVendPrimeSettlement:string
   */
  const makeDate = (date: string,isVendPrimeSettlement: string) => {
    return formatDate(new Date(Date.parse(date)))+";"+isVendPrimeSettlement;
  };

  const formatDate = (date: Date) => {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [month, day, year].join("/");
  };

  useEffect(() => {
    if (state && state.length > 0) return;
    GetVPSettlement(props.salesOrg, props.customerNum)
      .then((x) => {
        if (x.length < 1) return setIsError(true);
        setState(x);
      })
      .catch((_) => setIsError(true));
  }, [state]);

  if (isError) return <div />;
  if (!state || state.length < 1) return <div />;

  const datasets: Map<number, any> = new Map();

  const colorScheme = [
    "#25CCF7",
    "#FD7272",
    "#54a0ff",
    "#e67e22",
    "#27ae60",
    "#d63031",
    "#2ecc71",
    "#9b59b6",
    "#16a085",
    "#00d2d3",
    "#2980b9",
    "#8e44ad",
    "#f1c40f",
    "#1abc9c",
    "#e74c3c",
    "#f39c12",
    "#d35400",
    "#3498db",
    "#c0392b",
    "#55efc4",
    "#81ecec",
    "#74b9ff",
    "#a29bfe",
    "#dfe6e9",
    "#00b894",
    "#00cec9",
    "#0984e3",
    "#6c5ce7",
    "#ffeaa7",
    "#fab1a0",
    "#ff7675",
    "#fd79a8",
    "#fdcb6e",
    "#e17055",
    "#feca57",
    "#5f27cd",
    "#54a0ff",
    "#01a3a4"
  ];

  let index = 0;
  const orderQuantities: any = {};

  for (const i of state) {
    let found = datasets.get(i.material_num);

    if (!found) {
      const idx = index;
      index++;
      const color = colorScheme[idx];

      colorScheme.splice(idx, 1);

      found = {
        label: i.material_num_short + " " + i.description,
        fill: false,
        backgroundColor: color,
        fillColor: color,
        strokeColor: color,
        highlightFill: color,
        highlightStroke: color,
        borderColor: color,
        lineTension: 0.0,
        borderWidth: 1,
        data: []
      };
      datasets.set(i.material_num, found);
    }

    if (!orderQuantities[i.order_num]) {
      orderQuantities[i.order_num] = {
        date: i.visit_dt,
        quantity: i.quantity,
        seen: [i.material_num_short]
      };
    } else if (
      !orderQuantities[i.order_num].seen.includes(i.material_num_short)
    ) {
      orderQuantities[i.order_num].quantity += i.quantity;
      orderQuantities[i.order_num].seen.push(i.material_num_short);
    }

    datasets.set(i.material_num, {
      ...found,
      data: [...found.data, { x: makeDate(i.visit_dt,i.isVendPrimeSettlement), y: i.quantity }]
    });
  }

  let quantities: any = {
    data: []
  };

  Object.values(orderQuantities).forEach((v: any) => {
    quantities = {
      fill: false,
      backgroundColor: "black",
      fillColor: "black",
      strokeColor: "black",
      highlightFill: "black",
      highlightStroke: "black",
      borderColor: "black",
      label: "Total Volume",
      lineTension: 0.0,
      borderWidth: 2,
      data: [
        ...quantities.data,
        {
          x: makeDate(v.date,v.isVendPrimeSettlement),
          y: v.quantity
        }
      ]
    };
  });

  const opts = {
    labels: [...new Set(state.map((v) => makeDate(v.visit_dt,v.isVendPrimeSettlement)))],
    datasets: [...datasets].map((v) => v[1]).concat([quantities])
  };

  const wrapper = (children: any) =>
    props.hideAccordion ? (
      <div style={{ height: "400px" }}>{children}</div>
    ) : (
      <Accordion className={classes.root}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography>Settlements</Typography>
        </AccordionSummary>
        <AccordionDetails style={{ minHeight: 400 }}>
          {children}
        </AccordionDetails>
      </Accordion>
    );

  return wrapper(<Line
      data={opts}
      options={
        {
          maintainAspectRatio: false,
          scales: {
            yAxes: [
              {
                gridLines: {
                  display:false,
                  drawOnChartArea: false
                },
                ticks: {
                  beginAtZero: true
                }
              }
            ],
            /* This logic included for showing multiple x-Axes */
            xAxes:[{
              position: 'bottom',
              gridLines: {
                display:false,
                drawOnChartArea: false 
              },
              ticks: {
                callback(value:any):any {
                  return value.split(';')[0];
                }
              }
            },
              {
                gridLines: {
                  display:false,
                  drawOnChartArea: false 
                },
                position: 'bottom',
                ticks: {
                  callback(value:any):any {
                    return value.split(';')[1];
                  }
                }
              }
          ]
        },
          legend: {
            position: "bottom"
          },
          animation: false,
          tooltips: {
            callbacks: {
              label (tooltipItem: any, data: any) {
                return `${data.datasets[tooltipItem.datasetIndex].label}: ${
                  tooltipItem.value
                } Units`;
              }
            }
          },
       annotation: {
            autoColors: false,
            annotations: [
        {
          //       // Indicates the type of annotation
               type: "box",
              xMin: "01/12/2021",
               xMax: "04/14/2021",
              yMin: 0,
               yMax: 0,
              backgroundColor: "rgba(255, 99, 132, 1.0)"
              }
            ]
           } as any
        } as any
      }
                 />);
}
